import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

/* ------------------------------------------------------------------
   Academic Verification Panel (clean version)
   - Removes the Debug / Hide Debug button and all debug-info handling.
   ------------------------------------------------------------------ */

const AcademicVerificationPanel = () => {
  const navigate = useNavigate();

  // ------------------------ state ------------------------
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [notes, setNotes] = useState("");

  // ------------------ initial fetch + message timer ------------------
  useEffect(() => {
    fetchPendingVerifications();

    const timer = setTimeout(() => {
      setSuccessMessage("");
      setError("");
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  // ---------------------- helper: authorised API ---------------------
  const apiRequest = async (url, method = "GET", body = null) => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
      throw new Error("No authentication token found");
    }

    const options = {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    if (body && method !== "GET") options.body = JSON.stringify(body);

    const res = await fetch(url, options);
    if (res.status === 401) {
      localStorage.removeItem("token");
      navigate("/");
      throw new Error("Session expired. Please log in again.");
    }

    if (!res.ok) {
      const errTxt = await res.text();
      throw new Error(errTxt || `Request failed (${res.status})`);
    }
    return res.json();
  };

  // ------------------------- fetch list -------------------------
  const fetchPendingVerifications = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await apiRequest("/admin/pending-verifications");
      setPendingVerifications(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ------------------ approve / reject handler ------------------
  const handleVerification = async (approved) => {
    if (!selectedVerification) return;
    setLoading(true);
    setError("");
    setSuccessMessage("");
    try {
      const response = await apiRequest(
        "/admin/approve-verification",
        "POST",
        {
          id: selectedVerification.id,
          approved,
          notes,
        }
      );
      setSuccessMessage(
        response.message ||
          `Verification ${approved ? "approved" : "rejected"} successfully`
      );
      setSelectedVerification(null);
      setNotes("");
      fetchPendingVerifications();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --------------------------- ui helpers ----------------------------
  const formatDate = (str) => {
    if (!str) return "N/A";
    const d = new Date(str);
    return isNaN(d) ? str : d.toLocaleString();
  };
  const goBack = () => {
  localStorage.setItem("refreshVerificationCount", "true");
  navigate("/admin");
};

  // --------------------------- render -------------------------------
  return (
    <div style={{ padding: "1rem", maxWidth: 800, margin: "0 auto" }}>
      {/* header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <h1 style={{ fontSize: "1.5rem", margin: 0 }}>
          Academic Verification Panel
        </h1>
        <button
          onClick={goBack}
          style={{
            padding: "0.5rem 1rem",
            backgroundColor: "#2196F3",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Back to Dashboard
        </button>
      </div>

      {/* messages */}
      {successMessage && (
        <div
          style={{
            color: "green",
            marginBottom: "1rem",
            padding: "0.5rem",
            backgroundColor: "#e8f5e9",
            borderRadius: "4px",
          }}
        >
          {successMessage}
        </div>
      )}
      {error && (
        <div
          style={{
            color: "red",
            marginBottom: "1rem",
            padding: "0.5rem",
            backgroundColor: "#ffebee",
            borderRadius: "4px",
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div
          style={{
            padding: "2rem",
            textAlign: "center",
            border: "1px solid #ccc",
            borderRadius: "4px",
            backgroundColor: "#f9f9f9",
          }}
        >
          Loading verification data... Please wait.
        </div>
      ) : (
        <div style={{ display: "flex", gap: "1rem" }}>
          {/* list */}
          <div
            style={{
              flex: 1,
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "1rem",
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            <h2 style={{ fontSize: "1.2rem", marginTop: 0 }}>
              Pending Verifications
            </h2>
            <button
              onClick={fetchPendingVerifications}
              disabled={loading}
              style={{
                padding: "0.5rem 1rem",
                backgroundColor: "#4CAF50",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: loading ? "default" : "pointer",
                marginBottom: "1rem",
              }}
            >
              {loading ? "Loading..." : "Refresh List"}
            </button>
            {pendingVerifications.length === 0 ? (
              <p>No pending verifications found.</p>
            ) : (
              <ul style={{ listStyleType: "none", padding: 0 }}>
                {pendingVerifications.map((v) => (
                  <li
                    key={v.id}
                    onClick={() => setSelectedVerification(v)}
                    style={{
                      padding: "0.75rem",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      marginBottom: "0.5rem",
                      cursor: "pointer",
                      backgroundColor:
                        selectedVerification?.id === v.id ? "#e3f2fd" : "transparent",
                    }}
                  >
                    <div>
                      <strong>Email:</strong> {v.email}
                    </div>
                    <div>
                      <strong>Submitted:</strong> {formatDate(v.createdAt)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* details */}
          <div
            style={{
              flex: 1,
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "1rem",
            }}
          >
            <h2 style={{ fontSize: "1.2rem", marginTop: 0 }}>
              Verification Details
            </h2>
            {!selectedVerification ? (
              <p>Select a verification from the list to review.</p>
            ) : (
              <div>
                <div style={{ marginBottom: "1rem" }}>
                  <div>
                    <strong>Email:</strong> {selectedVerification.email}
                  </div>
                  <div>
                    <strong>Submitted:</strong> {formatDate(selectedVerification.createdAt)}
                  </div>
                  <div>
                    <strong>Document:</strong>{" "}
                    <a
                      href={selectedVerification.documentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Document
                    </a>
                  </div>
                </div>

                <label style={{ display: "block", marginBottom: "0.5rem" }}>
                  Admin Notes:
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  style={{
                    width: "100%",
                    height: "100px",
                    padding: "0.5rem",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    marginBottom: "1rem",
                  }}
                  placeholder="Add notes (optional)"
                />

                <div style={{ display: "flex", gap: "1rem" }}>
                  <button
                    onClick={() => handleVerification(true)}
                    disabled={loading}
                    style={{
                      flex: 1,
                      padding: "0.75rem",
                      backgroundColor: "#4CAF50",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: loading ? "default" : "pointer",
                    }}
                  >
                    Approve
                  </button>
                  <button 
                    onClick={() => handleVerification(false)}
                    disabled={loading}
                    style={{ 
                      flex: 1,
                      padding: "0.75rem",
                      backgroundColor: "#f44336",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: loading ? "default" : "pointer"
                    }}
                  >
                    Reject
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AcademicVerificationPanel;